import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import type { OverrideReason } from '../../types/codingAssistant';

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (args: { reason: OverrideReason; notes?: string }) => Promise<void>;
  originalCode: string | null;
  newCode: string | null;
  itemPhrase: string;
}

const REASON_OPTIONS: { value: OverrideReason; label: string }[] = [
  { value: 'better_match', label: 'A different code is more appropriate' },
  { value: 'extraction_error', label: 'The pipeline misidentified this action' },
  { value: 'clinical_context', label: 'Pipeline missed clinical context (e.g. bundling)' },
  { value: 'not_billable', label: 'This item should not generate a code' },
  { value: 'added_manually', label: 'I am adding a code the pipeline missed' },
  { value: 'removed', label: 'I am removing this code without replacement' },
  { value: 'other', label: 'Other (notes required)' },
];

export function CodeOverrideModal({
  open, onClose, onSubmit, originalCode, newCode, itemPhrase,
}: Props) {
  const [reason, setReason] = useState<OverrideReason>('better_match');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (reason === 'other' && !notes.trim()) return;
    setSubmitting(true);
    try {
      await onSubmit({ reason, notes: notes.trim() || undefined });
      onClose();
      setNotes('');
      setReason('better_match');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-semibold text-lg mb-2">Why are you changing this code?</h3>
            <p className="text-sm text-gray-600 mb-4">
              <span className="block">Item: <em>&quot;{itemPhrase}&quot;</em></span>
              <span className="block mt-1">
                {originalCode ?? '(none)'} &rarr; {newCode ?? '(removed)'}
              </span>
            </p>
            <div className="space-y-2 mb-4">
              {REASON_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="reason"
                    value={opt.value}
                    checked={reason === opt.value}
                    onChange={() => setReason(opt.value)}
                    className="mt-1"
                  />
                  <span className="text-sm">{opt.label}</span>
                </label>
              ))}
            </div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={reason === 'other' ? 'Required: describe why' : 'Notes (optional)'}
              className="w-full border rounded p-2 text-sm h-20 mb-4"
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || (reason === 'other' && !notes.trim())}
              >
                {submitting ? 'Saving\u2026' : 'Save override'}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
